import { ToastProvider, TooltipProvider } from '@clickt/brand';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { type AppRouter, buildRouter } from './router.tsx';
import { initSentry } from './sentry.ts';
import './styles.css';

// Bring Sentry up before anything else so unhandled errors during boot still report.
initSentry();

// A deploy mid-session deletes the previous build's hashed chunks, so a lazy
// route/chunk import in an already-open tab dies with a MIME/404 error. Vite
// surfaces that as `vite:preloadError`; reload onto the fresh build instead of
// stranding the click. The sessionStorage stamp stops a reload loop if the
// fresh build somehow still fails (max one auto-reload per 30s per tab).
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'clickt:chunk-reload-at';
  const last = Number(window.sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 30_000) return; // let the error surface normally
  window.sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry 4xx; they're contracted failures, not transients.
        if (
          error &&
          typeof error === 'object' &&
          'status' in error &&
          typeof (error as { status?: unknown }).status === 'number'
        ) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 1;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = buildRouter(queryClient);

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={250}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
